interface BlogPostData {
  id: number;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  tag: string;
  content: string;
}

export function getRelatedArticles(currentPost: BlogPostData, allPosts: BlogPostData[], maxResults: number = 3): BlogPostData[] {
  // Extract keywords from current post
  const currentKeywords = extractKeywords(currentPost);

  // Calculate relevance scores for all other posts
  const scoredPosts = allPosts
    .filter(post => post.id !== currentPost.id) // Exclude current post
    .map(post => ({
      post,
      score: calculateRelevanceScore(currentKeywords, post)
    }))
    .filter(item => item.score > 0) // Only include posts with some relevance
    .sort((a, b) => b.score - a.score) // Sort by score descending
    .slice(0, maxResults) // Take top N results
    .map(item => item.post);

  return scoredPosts;
}

function extractKeywords(post: BlogPostData): string[] {
  const keywords: string[] = [];

  // Add category and tag (high weight)
  keywords.push(post.category.toLowerCase());
  keywords.push(post.tag.toLowerCase());

  // Extract words from title (remove common words)
  const titleWords = post.title.toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Remove punctuation
    .split(/\s+/)
    .filter(word => word.length > 2 && !isCommonWord(word));

  keywords.push(...titleWords);

  // Extract key terms from excerpt (remove common words)
  const excerptWords = post.excerpt.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !isCommonWord(word))
    .slice(0, 5); // Limit to avoid too many keywords

  keywords.push(...excerptWords);

  // Extract key medical terms from content (first 500 characters)
  const contentSnippet = post.content.substring(0, 500).toLowerCase()
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !isCommonWord(word) && isMedicalTerm(word))
    .slice(0, 3); // Limit medical terms

  keywords.push(...contentSnippet);

  // Remove duplicates and return
  return [...new Set(keywords)];
}

function isCommonWord(word: string): boolean {
  const commonWords = new Set([
    'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'has', 'let', 'put', 'say', 'she', 'too', 'use', 'that', 'with', 'have', 'this', 'will', 'your', 'from', 'they', 'know', 'want', 'been', 'good', 'much', 'some', 'time', 'very', 'when', 'come', 'here', 'just', 'like', 'long', 'make', 'many', 'over', 'such', 'take', 'than', 'them', 'well', 'were'
  ]);
  return commonWords.has(word.toLowerCase());
}

function isMedicalTerm(word: string): boolean {
  const medicalTerms = new Set([
    'medical', 'interview', 'nhs', 'clinical', 'patient', 'doctor', 'hospital', 'health', 'care', 'treatment',
    'diagnosis', 'surgery', 'medicine', 'nursing', 'therapy', 'pharmacy', 'radiology', 'pathology', 'oncology',
    'cardiology', 'neurology', 'psychiatry', 'pediatrics', 'emergency', 'intensive', 'surgical', 'medical',
    'practitioner', 'consultant', 'specialist', 'trainee', 'resident', 'intern', 'fellow', 'registrar',
    'assessment', 'evaluation', 'examination', 'procedure', 'protocol', 'guideline', 'standard', 'competence',
    'communication', 'professionalism', 'ethics', 'safety', 'quality', 'governance', 'audit', 'research'
  ]);
  return medicalTerms.has(word.toLowerCase());
}

function calculateRelevanceScore(keywords: string[], post: BlogPostData): number {
  let score = 0;

  // Exact category match (highest weight)
  if (keywords.includes(post.category.toLowerCase())) {
    score += 50;
  }

  // Exact tag match (high weight)
  if (keywords.includes(post.tag.toLowerCase())) {
    score += 30;
  }

  // Title keyword matches
  const titleWords = post.title.toLowerCase().split(/\s+/);
  const titleMatches = keywords.filter(keyword => titleWords.includes(keyword));
  score += titleMatches.length * 10;

  // Excerpt keyword matches
  const excerptWords = post.excerpt.toLowerCase().split(/\s+/);
  const excerptMatches = keywords.filter(keyword => excerptWords.includes(keyword));
  score += excerptMatches.length * 5;

  // Content keyword matches (lower weight)
  const contentWords = post.content.toLowerCase()
    .replace(/<[^>]*>/g, '')
    .split(/\s+/);
  const contentMatches = keywords.filter(keyword => contentWords.includes(keyword));
  score += contentMatches.length * 2;

  return score;
}
